import { FunctionalComponent, h } from "preact";
import style from "./style.css";
import Button from "../../../../components/Button/Button";
import DataTable from "../../../../components/DataTable/DataTable";
import { useListExchanges } from "../../../../hooks/exchange";
import Loader from "../../../../components/Loader/Loader";
import { TableColumn } from "../../../../components/DataTable/types";
import { formatDate, getMe } from "../../../../util";
import { route } from "preact-router";
import { RoutePath } from "../../../../models/route";
import Tag, { TagType } from "../../../../components/Tag/Tag";
import Icon from "../../../../components/Icon/Icon";
import { ExchangeStatus } from "../../../../models/exchange";
import ExchangeStatusFilter from "./components/ExchangeStatusFilter/ExchangeStatusFilter";
import { useMemo } from "preact/hooks";
import Tooltip from "../../../../components/Tooltip/Tooltip";

const ExchangesDesktop: FunctionalComponent = () => {
  const {
    fetchingExchanges,
    exchanges,
    actions: { filterByStatus, sortByDate },
  } = useListExchanges({ date: "desc" });
  const meId = getMe().id;

  const getTagStatus = (status: ExchangeStatus): TagType => {
    switch (status) {
      case "accepted":
        return "green";
      case "declined":
        return "red";
      case "confirmed":
        return "disabled";
      case "pending":
        return "grey";
    }
  };

  const columns: TableColumn[] = [
    {
      title: "Date",
      dataIndex: "date",
      defaultSortOrder: "desc",
      onSort: (order) => sortByDate(order),
      render: (date, index) => {
        const exchange = exchanges.find((_, i) => i === index);
        const isRequester = exchange?.requester.id === meId;
        return (
          <div class={style.dateCellWrapper}>
            {date ? (
              isRequester ? (
                <Tooltip text="Outgoing request">
                  <Icon
                    icon="top-left-arrow"
                    iconWrapperClass={style.exchangeTypeIcon}
                  />
                </Tooltip>
              ) : (
                <Tooltip text="Incoming request">
                  <Icon
                    icon="bottom-right-arrow"
                    iconWrapperClass={style.exchangeTypeIcon}
                  />
                </Tooltip>
              )
            ) : null}
            {date}
          </div>
        );
      },
    },
    {
      title: "Requested living sanctuary",
      dataIndex: "requestedHome",
      render: (value) => {
        if (value.length === 1) {
          return <div>{value[0].name}</div>;
        }
        return <div>{value.length} living sancutaries</div>;
      },
    },
    {
      title: "Requested date range",
      dataIndex: "requestedDateRange",
    },
    {
      title: "Offered living sanctuary",
      dataIndex: "offeredHome",
      render: (value) => {
        return <div>{value.name}</div>;
      },
    },
    {
      title: "Offered date range",
      dataIndex: "offeredDateRange",
    },
    {
      title: "Duration",
      dataIndex: "duration",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value) => {
        return <Tag tagType={getTagStatus(value)}>{value}</Tag>;
      },
      filter: <ExchangeStatusFilter onChange={filterByStatus} />,
    },
    {
      key: "actions",
      render: (value) => {
        const hasMultipleChildren = value.children?.length > 1;
        if (hasMultipleChildren) return <span />;

        return (
          <div class={style.moreIconWrapper}>
            <Icon
              icon="more"
              onClick={() => route(`/owner/exchanges/${value.id}`, true)}
            />
          </div>
        );
      },
    },
  ];

  const pagination = {
    totalEntriesPerPage: 8,
  };

  console.log(exchanges);

  const data = useMemo(() => {
    let uniqueExchanges: any[] = [];

    exchanges.forEach((exchange) => {
      console.log("ex", exchange);

      const isUniqueExchange = uniqueExchanges.some(
        (e) => e.groupId === exchange.groupId
      );

      const sanitizedExchange = {
        id: exchange.id,
        requestedHome: [exchange.requesteeHome],
        requestedDateRange: `${formatDate(
          exchange.requesteeStartDate,
          "MMM D, YYYY"
        )} - ${formatDate(exchange.requesteeEndDate, "MMM D, YYYY")}`,
        offeredHome: exchange.requesterHome,
        offeredDateRange: `${formatDate(
          exchange.requesterStartDate,
          "MMM D, YYYY"
        )} - ${formatDate(exchange.requesterEndDate, "MMM D, YYYY")}`,
        duration: `${exchange.duration} days`,
        status: exchange.status,
        groupId: exchange.groupId,
      };

      if (isUniqueExchange) {
        uniqueExchanges = uniqueExchanges.map((uniqueExchange) => {
          if (uniqueExchange.groupId === exchange.groupId) {
            return {
              ...uniqueExchange,
              requestedHome: [
                ...uniqueExchange.requestedHome,
                exchange.requesteeHome,
              ],
              children: [...uniqueExchange.children, sanitizedExchange],
            };
          }
          return uniqueExchange;
        });

        return;
      }

      uniqueExchanges = [
        ...uniqueExchanges,
        {
          ...sanitizedExchange,
          date: formatDate(exchange.createdAt, "MMM D, YY"),
          children: [sanitizedExchange],
        },
      ];
    });

    return uniqueExchanges;
  }, [exchanges]);

  console.log("data", data);

  return (
    <div class={style.viewWrapper}>
      <div class={style.viewHeader}>
        <h2>Exchanges</h2>
        <Button
          iconLeft="plus"
          buttonType="primary"
          buttonClass={style.toCreateExchangeRequestButton}
          onClick={() => route(RoutePath.OwnerCreateExchangeRequest, true)}
        >
          New Exchange
        </Button>
      </div>
      <div class={style.tableWrapper}>
        {fetchingExchanges ? (
          <Loader />
        ) : (
          <DataTable
            columns={columns}
            dataSource={data}
            pagination={pagination}
          />
        )}
      </div>
    </div>
  );
};

export default ExchangesDesktop;
